// Design System Tokens — Apple-inspired refinements
export const designTokens = {
    // Color Palette — iOS-aligned semantic colors
    colors: {
        // Primary (Slate base with Apple Blue accent)
        primary: {
            main: '#475569',    // Slate 600
            light: '#64748b',   // Slate 500
            dark: '#1e293b',    // Slate 800
            accent: '#007AFF',  // Apple System Blue
            gradient: 'linear-gradient(135deg, #1e293b 0%, #6366f1 100%)',
        },
        // Semantic Colors — Apple Human Interface Guidelines
        success: {
            main: '#34C759',    // Apple System Green
            light: '#4CD964',
            dark: '#248A3D',
            gradient: 'linear-gradient(135deg, #1e293b 0%, #34C759 100%)',
        },
        warning: {
            main: '#FF9500',    // Apple System Orange
            light: '#FFAA33',
            dark: '#C93400',
            gradient: 'linear-gradient(135deg, #1e293b 0%, #FF9500 100%)',
        },
        error: {
            main: '#FF3B30',    // Apple System Red
            light: '#FF6961',
            dark: '#D70015',
            gradient: 'linear-gradient(135deg, #1e293b 0%, #FF3B30 100%)',
        },
        info: {
            main: '#007AFF',    // Apple System Blue
            light: '#5AC8FA',   // Apple System Teal
            dark: '#0040DD',
            gradient: 'linear-gradient(135deg, #1e293b 0%, #007AFF 100%)',
        },
        // Neutral Grays — Premium Slate
        background: {
            default: '#0f172a', // Deep slate
            paper: '#1e293b',   // Card surface
            elevated: '#334155', // Elevated surface
            subtle: 'rgba(30, 41, 59, 0.8)',
        },
        text: {
            primary: '#f8fafc',   // Near-white
            secondary: '#94a3b8', // Muted
            disabled: '#475569',  // Low contrast
            tertiary: '#64748b',  // Hint text
        },
        // Extended palette for charts & tags
        chart: {
            blue: '#007AFF',
            green: '#34C759',
            orange: '#FF9500',
            red: '#FF3B30',
            purple: '#AF52DE',  // Apple System Purple
            teal: '#5AC8FA',    // Apple System Teal
            indigo: '#5856D6',  // Apple System Indigo
            pink: '#FF2D55',    // Apple System Pink
            yellow: '#FFCC00',  // Apple System Yellow
            mint: '#00C7BE',    // Apple Mint
        },
    },

    // Typography — SF Pro-inspired stack
    typography: {
        fontFamily: '"SF Pro Display", "SF Pro Text", "Outfit", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: {
            xs: '0.6875rem',   // 11px — Caption 2
            sm: '0.8125rem',   // 13px — Footnote
            base: '0.9375rem', // 15px — Body (Apple standard)
            lg: '1.0625rem',   // 17px — Headline
            xl: '1.25rem',     // 20px — Title 3
            '2xl': '1.375rem', // 22px — Title 2
            '3xl': '1.75rem',  // 28px — Title 1
            '4xl': '2.125rem', // 34px — Large Title
        },
        fontWeight: {
            normal: 400,  // Alias for backward compatibility
            regular: 400,
            medium: 500,
            semibold: 600,
            bold: 700,
            heavy: 800,
        },
        lineHeight: {
            tight: 1.2,
            normal: 1.47, // Apple's standard line height ratio
            relaxed: 1.6,
        },
        letterSpacing: {
            tight: '-0.022em',  // Apple title tracking
            normal: '-0.011em', // Apple body tracking
            wide: '0.06em',     // Uppercase labels
        },
    },

    // Spacing — 8pt grid
    spacing: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '32px',
        '2xl': '48px',
        '3xl': '64px',
    },

    // Border Radius — Apple continuous corners
    borderRadius: {
        sm: '8px',
        md: '12px',
        lg: '16px',
        xl: '20px',
        '2xl': '28px',
        full: '9999px',
    },

    // Shadows — Depth layers
    shadows: {
        sm: '0 1px 3px 0 rgba(0, 0, 0, 0.12)',
        md: '0 4px 12px rgba(0, 0, 0, 0.15)',
        lg: '0 8px 24px rgba(0, 0, 0, 0.2)',
        xl: '0 16px 48px rgba(0, 0, 0, 0.25)',
        glass: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
        glow: '0 0 24px rgba(0, 122, 255, 0.25)',  // Blue glow
        card: '0 2px 16px 0 rgba(0, 0, 0, 0.12)',
        focus: '0 0 0 4px rgba(0, 122, 255, 0.3)',  // Apple focus ring
    },

    // Glassmorphism — Apple-exact values
    glassmorphism: {
        background: 'rgba(29, 29, 31, 0.72)',            // Apple Dark translucent
        backgroundLight: 'rgba(255, 255, 255, 0.72)',     // Apple Light translucent
        backdropFilter: 'saturate(180%) blur(20px)',       // Apple's exact values
        border: '1px solid rgba(255, 255, 255, 0.12)',    // Subtle edge
        borderLight: '1px solid rgba(0, 0, 0, 0.08)',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.12)',
    },

    // Breakpoints
    breakpoints: {
        xs: '0px',
        sm: '600px',
        md: '960px',
        lg: '1280px',
        xl: '1920px',
    },

    // Transitions — Apple spring dynamics
    transitions: {
        fast: '200ms cubic-bezier(0.25, 0.1, 0.25, 1)',     // Quick interactions
        normal: '350ms cubic-bezier(0.25, 0.1, 0.25, 1)',   // Standard
        slow: '500ms cubic-bezier(0.25, 0.1, 0.25, 1)',     // Emphasis
        spring: '500ms cubic-bezier(0.34, 1.56, 0.64, 1)',  // Bouncy (for modals, popovers)
        easeOut: '300ms cubic-bezier(0, 0, 0.2, 1)',         // Deceleration
    },

    // Motion — Framer Motion spring presets
    motion: {
        spring: { type: 'spring', stiffness: 300, damping: 30 },
        springBouncy: { type: 'spring', stiffness: 400, damping: 25 },
        springGentle: { type: 'spring', stiffness: 200, damping: 35 },
        fadeIn: { initial: { opacity: 0 }, animate: { opacity: 1 } },
        slideUp: { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } },
        scaleIn: { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 } },
    },

    // Interactive states
    interactions: {
        hoverLift: {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
        },
        pressScale: {
            transform: 'scale(0.97)',
        },
        focusRing: {
            outline: 'none',
            boxShadow: '0 0 0 4px rgba(0, 122, 255, 0.3)',
        },
    },
};

export default designTokens;
